import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, Building2, Eye, Plus, RotateCcw, XCircle } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { PASSWORD_MIN } from '../../../components/auth/credentialSchema';
import AdminModal from '../../components/AdminModal';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import StatusBadge from '../../components/StatusBadge';
import StatusFilter, { LifecycleStatusFilter } from '../../components/StatusFilter';
import ListPager from '../../components/ListPager';
import AdminListToolbar from '../../components/AdminListToolbar';
import MultiSelectFilter from '../../components/MultiSelectFilter';
import BulkActionsBar, { bulkResultMessage } from '../../components/BulkActionsBar';
import { AdminListResult, useAdminList } from '../../hooks/useAdminList';
import { useRowSelection } from '../../hooks/useRowSelection';
import { formatDate } from '../../lib/format';
import { searchActiveOrganizations } from '../../lib/pickers';
import { startUserSimulation } from '../../lib/simulation';
import { PeopleIcon } from '../../../components/icons';

interface OrganizationMembership {
  id: string;
  role: string;
  member_type: string;
  organization: {
    id: string;
    name: string;
    subscription: string | null;
  };
}

interface AdminUserRow {
  id: string;
  username: string | null;
  displayName: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  daysSinceLogin: number | null;
  isActive: boolean;
  deactivatedAt: string | null;
  organizationCount: number;
  organizationMemberships: OrganizationMembership[];
}

const parseUsers = (raw: unknown): AdminListResult<AdminUserRow> => {
  const data = raw as {
    users?: AdminUserRow[];
    pagination?: { total?: number; hasMore?: boolean };
  };
  return {
    rows: data.users ?? [],
    total: data.pagination?.total ?? 0,
    hasMore: data.pagination?.hasMore ?? false,
  };
};

const displayName = (user: AdminUserRow): string => {
  if (user.displayName) return user.displayName;
  const composed = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return composed || user.username || user.email || 'Unknown user';
};

const lastLoginLabel = (user: AdminUserRow): string => {
  if (user.daysSinceLogin == null) return 'Never';
  const days = Math.floor(user.daysSinceLogin);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

const isSystemAdmin = (user: AdminUserRow): boolean =>
  user.organizationMemberships.some((membership) => membership.role?.toLowerCase() === 'system_admin');

const filterSelectClasses =
  'h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';

const COLUMN_COUNT = 8;

const EMPTY_CREATE_FORM = {
  email: '',
  password: '',
  username: '',
  firstName: '',
  lastName: '',
  displayName: '',
  createAsCitizen: false,
};

const UsersView: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LifecycleStatusFilter>('active');
  const [orgFilterIds, setOrgFilterIds] = useState<string[]>([]);
  const [memberType, setMemberType] = useState('');
  const [deactivateTarget, setDeactivateTarget] = useState<AdminUserRow | null>(null);
  const [reactivateTarget, setReactivateTarget] = useState<AdminUserRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [manualPasswordOpen, setManualPasswordOpen] = useState(false);
  const [createdUser, setCreatedUser] = useState<{
    id: string;
    resetUrl: string | null;
    email: string;
    emailSent?: boolean;
  } | null>(null);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const selection = useRowSelection();
  const [bulkAction, setBulkAction] = useState<'deactivate' | 'reactivate' | null>(null);
  const [bulkOrgOpen, setBulkOrgOpen] = useState(false);
  const [bulkOrgForm, setBulkOrgForm] = useState({ organizationId: '', memberType: 'staff' });
  const [simulateTarget, setSimulateTarget] = useState<AdminUserRow | null>(null);
  const [simulating, setSimulating] = useState(false);

  const list = useAdminList<AdminUserRow>({
    queryKey: 'accounts-users',
    endpoint: '/admin/users',
    parse: parseUsers,
    filters: {
      status,
      organization_ids: orgFilterIds.length > 0 ? orgFilterIds.join(',') : undefined,
      // Combines with the organization filter server-side: membership of this
      // type in one of the selected organizations.
      member_type: memberType || undefined,
    },
    initialSort: { key: 'created_at', direction: 'desc' },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-list'] });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.put(`/admin/accounts/users/${id}/deactivate`),
    onSuccess: () => {
      toast.success('User deactivated');
      setDeactivateTarget(null);
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to deactivate user');
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => api.put(`/admin/accounts/users/${id}/reactivate`),
    onSuccess: () => {
      toast.success('User reactivated');
      setReactivateTarget(null);
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to reactivate user');
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/users', {
        email: createForm.email.trim(),
        // Blank → the backend generates a random, never-displayed credential
        // and returns a one-time setup link (ADMINTOOL_USER_PASSWORDS.md §3.5)
        password: createForm.password || undefined,
        username: createForm.username.trim() || undefined,
        firstName: createForm.firstName.trim() || undefined,
        lastName: createForm.lastName.trim() || undefined,
        displayName: createForm.displayName.trim() || undefined,
        createAsCitizen: createForm.createAsCitizen,
      }),
    onSuccess: (response) => {
      toast.success('User created');
      invalidate();
      const newUserId = response.data?.id as string | undefined;
      const resetUrl = response.data?.reset_url as string | null | undefined;
      if (newUserId && resetUrl) {
        // Keep the modal open in its success state so the admin can deliver
        // the setup link before leaving.
        setCreatedUser({ id: newUserId, resetUrl, email: createForm.email.trim() });
        setCreateForm(EMPTY_CREATE_FORM);
        return;
      }
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE_FORM);
      if (newUserId) navigate(`/admin-next/accounts/users/${newUserId}`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to create user');
    },
  });

  const sendWelcomeMutation = useMutation({
    mutationFn: (userId: string) =>
      api.post(`/admin/accounts/users/${userId}/password-reset`, { deliver: 'email' }),
    onSuccess: (response) => {
      toast.success(`Setup email sent to ${response.data.data?.email ?? 'the user'}`);
      // Sending mints a fresh token — the previously shown link is superseded.
      setCreatedUser((current) => (current ? { ...current, resetUrl: null, emailSent: true } : current));
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'Failed to send setup email'),
  });

  const closeCreateModal = () => {
    const created = createdUser;
    setCreateOpen(false);
    setCreatedUser(null);
    setManualPasswordOpen(false);
    if (created) navigate(`/admin-next/accounts/users/${created.id}`);
  };

  const copyResetLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Setup link copied');
    } catch {
      toast.error('Could not copy — select and copy the link text');
    }
  };

  const canSubmitCreate =
    createForm.email.trim().length > 0 &&
    (createForm.password.length === 0 || createForm.password.length >= PASSWORD_MIN);

  const bulkLifecycleMutation = useMutation({
    mutationFn: (action: 'deactivate' | 'reactivate') =>
      api.post('/admin/accounts/users/bulk-lifecycle', { user_ids: selection.ids, action }),
    onSuccess: (response, action) => {
      toast.success(bulkResultMessage(`${action}d`, response.data.data));
      setBulkAction(null);
      selection.clear();
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Bulk operation failed');
    },
  });

  const bulkAddToOrgMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/accounts/users/bulk-add-to-organization', {
        user_ids: selection.ids,
        organization_id: bulkOrgForm.organizationId,
        member_type: bulkOrgForm.memberType,
      }),
    onSuccess: (response) => {
      toast.success(bulkResultMessage('added', response.data.data));
      setBulkOrgOpen(false);
      setBulkOrgForm({ organizationId: '', memberType: 'staff' });
      selection.clear();
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Bulk operation failed');
    },
  });

  // Active organizations — feeds both the toolbar filter and the bulk add-to-org modal
  const orgOptions = useQuery({
    queryKey: ['admin-accounts', 'active-org-options'],
    queryFn: async () => {
      const response = await api.get('/admin/accounts/organizations', {
        params: { status: 'active', limit: 100 },
      });
      return (response.data.data.organizations as Array<{ id: string; name: string }>).map(
        ({ id, name }) => ({ id, name })
      );
    },
    staleTime: 5 * 60 * 1000,
  });

  const pageIds = list.rows.map((user) => user.id);

  return (
    <div className="space-y-4">
      <AdminListToolbar
        search={list.search}
        onSearch={list.setSearch}
        searchPlaceholder="Search by name, email, or username…"
        searchAriaLabel="Search users"
        actions={
          <Button variant="primary" size="sm" icon={Plus} onClick={() => setCreateOpen(true)}>
            Add User
          </Button>
        }
      >
        <StatusFilter value={status} onChange={setStatus} />
        <select
          value={memberType}
          onChange={(event) => setMemberType(event.target.value)}
          aria-label="Filter by membership type"
          className={filterSelectClasses}
        >
          <option value="">All types</option>
          <option value="staff">Staff</option>
          <option value="citizen">Citizen</option>
        </select>
        <MultiSelectFilter
          label="Organizations"
          fetchOptions={searchActiveOrganizations}
          selectedIds={orgFilterIds}
          onChange={setOrgFilterIds}
        />
      </AdminListToolbar>

      <BulkActionsBar count={selection.count} noun="user" onClear={selection.clear}>
        <Button variant="outline" size="sm" icon={XCircle} onClick={() => setBulkAction('deactivate')}>
          Deactivate
        </Button>
        <Button variant="outline" size="sm" icon={RotateCcw} onClick={() => setBulkAction('reactivate')}>
          Reactivate
        </Button>
        <Button variant="outline" size="sm" icon={Building2} onClick={() => setBulkOrgOpen(true)}>
          Add to organization…
        </Button>
      </BulkActionsBar>

      {list.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load users. Please try again.
          </p>
          <Button variant="outline" size="sm" onClick={() => list.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <StandardTable horizontalScroll loading={list.isFetching && !list.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell isCheckbox>
                <input
                  type="checkbox"
                  aria-label="Select all users on this page"
                  checked={selection.allSelected(pageIds)}
                  onChange={(event) => selection.setPage(pageIds, event.target.checked)}
                />
              </StandardTableHeaderCell>
              <StandardTableHeaderCell sortable sortKey="display_name" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Name
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md" sortable sortKey="email" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Email
              </StandardTableHeaderCell>
              <StandardTableHeaderCell sortable sortKey="organization_count" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Organizations
              </StandardTableHeaderCell>
              <StandardTableHeaderCell sortable sortKey="is_active" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Status
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg" columnType="fit" sortable sortKey="last_login_at" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Last Login
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg" columnType="fit" sortable sortKey="created_at" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Created
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="actions">Actions</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {list.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : list.rows.length === 0 ? (
              <EmptyTableState
                colSpan={COLUMN_COUNT}
                message={list.search ? `No users match "${list.search}"` : 'No users found'}
                icon={<PeopleIcon className="h-8 w-8" />}
              />
            ) : (
              list.rows.map((user) => (
                <StandardTableRow
                  key={user.id}
                  selected={selection.has(user.id)}
                  onClick={() => navigate(`/admin-next/accounts/users/${user.id}`)}
                >
                  <StandardTableCell isCheckbox>
                    <div onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${displayName(user)}`}
                        checked={selection.has(user.id)}
                        onChange={() => selection.toggle(user.id)}
                      />
                    </div>
                  </StandardTableCell>
                  <StandardTableCell>
                    <div className="font-medium text-text-primary dark:text-text-dark-primary">
                      {displayName(user)}
                    </div>
                    {user.username && (
                      <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        @{user.username}
                      </div>
                    )}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">{user.email ?? '—'}</StandardTableCell>
                  <StandardTableCell>
                    {user.organizationMemberships.length === 0 ? (
                      <span className="text-text-tertiary dark:text-text-dark-tertiary">None</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {user.organizationMemberships.slice(0, 2).map((membership) => (
                          <span
                            key={membership.id}
                            className="rounded-full bg-background-secondary px-2 py-0.5 text-xs text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary"
                            title={`${membership.organization.name} — ${membership.role}`}
                          >
                            {membership.organization.name}
                          </span>
                        ))}
                        {user.organizationMemberships.length > 2 && (
                          <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                            +{user.organizationMemberships.length - 2} more
                          </span>
                        )}
                      </div>
                    )}
                  </StandardTableCell>
                  <StandardTableCell>
                    <StatusBadge active={user.isActive !== false} />
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{lastLoginLabel(user)}</StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{formatDate(user.createdAt)}</StandardTableCell>
                  <StandardTableCell>
                    <div className="flex items-center" onClick={(event) => event.stopPropagation()}>
                      {user.isActive !== false && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Eye}
                          disabled={isSystemAdmin(user)}
                          title={isSystemAdmin(user) ? 'System administrators cannot be simulated' : 'View the app as this user'}
                          onClick={() => setSimulateTarget(user)}
                          ariaLabel={`Simulate ${displayName(user)}`}
                        >
                          Simulate
                        </Button>
                      )}
                      {user.isActive !== false ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={XCircle}
                          disabled={isSystemAdmin(user)}
                          title={isSystemAdmin(user) ? 'System administrators cannot be deactivated' : undefined}
                          onClick={() => setDeactivateTarget(user)}
                          ariaLabel={`Deactivate ${displayName(user)}`}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={RotateCcw}
                          onClick={() => setReactivateTarget(user)}
                          ariaLabel={`Reactivate ${displayName(user)}`}
                        >
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      {!list.isError && (
        <ListPager
          offset={list.offset}
          limit={list.limit}
          total={list.total}
          page={list.page}
          pageCount={list.pageCount}
          hasPrev={list.hasPrev}
          hasNext={list.hasNext}
          onPrev={list.prevPage}
          onNext={list.nextPage}
          noun="users"
        />
      )}

      <AdminModal
        isOpen={createOpen}
        title={createdUser ? 'User Created' : 'Add User'}
        onClose={closeCreateModal}
        footer={
          createdUser ? (
            <Button variant="primary" size="sm" onClick={closeCreateModal}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={closeCreateModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!canSubmitCreate}
                loading={createMutation.isLoading}
                onClick={() => createMutation.mutate()}
              >
                Create User
              </Button>
            </>
          )
        }
      >
        {createdUser ? (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              The account was created without a usable password. Deliver the setup link below, or
              email it — the user sets their own password from it (valid 24 hours, single use).
            </p>
            {createdUser.resetUrl ? (
              <div className="flex items-center gap-2 rounded-lg border border-border-light bg-background-secondary p-2 dark:border-border-dark dark:bg-background-dark-tertiary">
                <code className="min-w-0 flex-1 break-all text-xs text-text-primary dark:text-text-dark-primary">
                  {createdUser.resetUrl}
                </code>
                <Button variant="outline" size="sm" onClick={() => copyResetLink(createdUser.resetUrl!)}>
                  Copy
                </Button>
              </div>
            ) : (
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                {createdUser.emailSent
                  ? `Setup email sent to ${createdUser.email}.`
                  : 'The setup link is no longer available.'}
              </p>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={createdUser.email === '' || createdUser.emailSent === true}
              loading={sendWelcomeMutation.isLoading}
              onClick={() => sendWelcomeMutation.mutate(createdUser.id)}
            >
              Send setup email{createdUser.email ? ` to ${createdUser.email}` : ''}
            </Button>
            {createdUser.resetUrl && (
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Sending the email issues a fresh link and replaces the one shown above.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {(
              [
                { key: 'email', label: 'Email *', type: 'email', autoComplete: 'off' },
                { key: 'displayName', label: 'Display Name', type: 'text', autoComplete: 'off' },
                { key: 'firstName', label: 'First Name', type: 'text', autoComplete: 'off' },
                { key: 'lastName', label: 'Last Name', type: 'text', autoComplete: 'off' },
                { key: 'username', label: 'Username', type: 'text', autoComplete: 'off' },
              ] as const
            ).map((field) => (
              <div key={field.key}>
                <label htmlFor={`create-user-${field.key}`} className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                  {field.label}
                </label>
                <input
                  id={`create-user-${field.key}`}
                  type={field.type}
                  autoComplete={field.autoComplete}
                  value={createForm[field.key]}
                  onChange={(event) => setCreateForm((current) => ({ ...current, [field.key]: event.target.value }))}
                  className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
                />
              </div>
            ))}
            <label className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
              <input
                type="checkbox"
                checked={createForm.createAsCitizen}
                onChange={(event) => setCreateForm((current) => ({ ...current, createAsCitizen: event.target.checked }))}
              />
              Create as citizen account (Citizens-org membership + personal workspace)
            </label>
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              The account is created without a usable password — you'll get a one-time setup link
              to deliver (or email) on the next screen. To invite by email instead, use
              &quot;Invite by email&quot; on an organization&apos;s Members tab.
            </p>
            <div>
              <button
                type="button"
                className="text-xs font-medium text-text-tertiary underline hover:text-text-secondary dark:text-text-dark-tertiary dark:hover:text-text-dark-secondary"
                onClick={() => setManualPasswordOpen((current) => !current)}
              >
                {manualPasswordOpen ? 'Hide manual password' : 'Set password manually (last resort)'}
              </button>
              {manualPasswordOpen && (
                <div className="mt-2 space-y-1">
                  <label htmlFor="create-user-password" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                    Password (min {PASSWORD_MIN} characters)
                  </label>
                  {/* 'new-password' stops browsers autofilling the admin's own
                      saved credentials (autocomplete="off" is ignored here) */}
                  <input
                    id="create-user-password"
                    type="password"
                    autoComplete="new-password"
                    value={createForm.password}
                    onChange={(event) => setCreateForm((current) => ({ ...current, password: event.target.value }))}
                    className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
                  />
                  <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    You'll know this credential — prefer the setup link. Ask the user to change
                    their password after first login.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </AdminModal>

      <ConfirmationDialog
        isOpen={deactivateTarget !== null}
        title="Deactivate user"
        message={
          deactivateTarget
            ? `Deactivate ${displayName(deactivateTarget)}? They will be signed out and unable to log in until reactivated. Their organization memberships and workspace access are preserved. Nothing is deleted.`
            : ''
        }
        confirmText="Deactivate"
        variant="warning"
        onConfirm={() => deactivateTarget && deactivateMutation.mutate(deactivateTarget.id)}
        onCancel={() => setDeactivateTarget(null)}
      />

      <ConfirmationDialog
        isOpen={reactivateTarget !== null}
        title="Reactivate user"
        message={
          reactivateTarget
            ? `Reactivate ${displayName(reactivateTarget)}? They regain login access with all previous memberships and roles.`
            : ''
        }
        confirmText="Reactivate"
        variant="info"
        onConfirm={() => reactivateTarget && reactivateMutation.mutate(reactivateTarget.id)}
        onCancel={() => setReactivateTarget(null)}
      />

      <ConfirmationDialog
        isOpen={simulateTarget !== null}
        title="Simulate user"
        message={
          simulateTarget
            ? `View the application as ${displayName(simulateTarget)} (${simulateTarget.email ?? 'no email'})? You'll see exactly what they see, with their permissions. An amber banner stays on screen until you exit the simulation, and the session is logged. Sessions expire after 2 hours.`
            : ''
        }
        confirmText={simulating ? 'Starting…' : 'Start Simulation'}
        variant="info"
        onConfirm={async () => {
          if (simulating || !simulateTarget) return;
          setSimulating(true);
          try {
            await startUserSimulation(simulateTarget.id, '/admin-next/accounts?tab=users');
          } catch (error: any) {
            toast.error(error?.response?.data?.error || 'Failed to start simulation');
            setSimulating(false);
          }
        }}
        onCancel={() => setSimulateTarget(null)}
      />

      <ConfirmationDialog
        isOpen={bulkAction !== null}
        title={bulkAction === 'deactivate' ? 'Deactivate selected users' : 'Reactivate selected users'}
        message={
          bulkAction === 'deactivate'
            ? `Deactivate ${selection.count} selected user${selection.count === 1 ? '' : 's'}? They will be signed out and unable to log in until reactivated. Your own account, system administrators, and already-deactivated users are skipped automatically. Nothing is deleted.`
            : `Reactivate ${selection.count} selected user${selection.count === 1 ? '' : 's'}? They regain login access with all previous memberships and roles. Already-active users are skipped.`
        }
        confirmText={bulkAction === 'deactivate' ? 'Deactivate All' : 'Reactivate All'}
        variant={bulkAction === 'deactivate' ? 'warning' : 'info'}
        onConfirm={() => bulkAction && bulkLifecycleMutation.mutate(bulkAction)}
        onCancel={() => setBulkAction(null)}
      />

      <AdminModal
        isOpen={bulkOrgOpen}
        title={`Add ${selection.count} user${selection.count === 1 ? '' : 's'} to organization`}
        onClose={() => setBulkOrgOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setBulkOrgOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={bulkOrgForm.organizationId === ''}
              loading={bulkAddToOrgMutation.isLoading}
              onClick={() => bulkAddToOrgMutation.mutate()}
            >
              Add to Organization
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="bulk-org" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              Organization *
            </label>
            <select
              id="bulk-org"
              value={bulkOrgForm.organizationId}
              onChange={(event) => setBulkOrgForm((current) => ({ ...current, organizationId: event.target.value }))}
              className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
            >
              <option value="">Select an active organization…</option>
              {(orgOptions.data ?? []).map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="bulk-member-type" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              Membership type
            </label>
            <select
              id="bulk-member-type"
              value={bulkOrgForm.memberType}
              onChange={(event) => setBulkOrgForm((current) => ({ ...current, memberType: event.target.value }))}
              className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
            >
              <option value="staff">Staff (org_admin, gets a personal workspace)</option>
              <option value="citizen">Citizen affiliation</option>
            </select>
          </div>
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            Users who are deactivated or already members of this organization are skipped automatically.
          </p>
        </div>
      </AdminModal>
    </div>
  );
};

export default UsersView;
